/**
 * Screenshot helper for the feedback popover (ADR-0074).
 *
 * Captures the visible viewport via html2canvas-pro (the v1 html2canvas
 * library couldn't parse modern CSS color() functions used in our styles).
 * Returns null on failure so the popover can flag screenshot_capture_failed
 * = true and submit anyway.
 *
 * No client-side PII redaction in v1 — the input-blanking pass was pulled
 * after dev-smoke surfaced two problems: (a) the browser silently rejects
 * placeholder bullets on number/email/url/tel input types, leaving real
 * values un-redacted while implying otherwise; (b) form inputs aren't the
 * sensitive surface anyway — query results in tables, customer names in
 * cells, and similar rendered content are. Both belong to a future PII
 * pass with an opt-in `.feedback-private` class.
 */

export interface ScreenshotResult {
  mime_type: "image/png";
  base64_bytes: string;
}

export async function captureScreenshot(): Promise<ScreenshotResult | null> {
  if (typeof document === "undefined") return null;

  try {
    const mod = await import("html2canvas-pro");
    const html2canvas = (mod as unknown as { default: (el: HTMLElement, opts?: Record<string, unknown>) => Promise<HTMLCanvasElement> }).default;
    const fullCanvas = await html2canvas(document.body, {
      backgroundColor: null,
      logging: false,
      useCORS: true,
      // The onclone hook runs against the cloned DOM only — live DOM is
      // untouched. html2canvas renders <textarea> as a single inline
      // string ignoring textarea wrapping, so we replace each one with a
      // div that has the same width + proper wrap CSS.
      onclone: (clonedDoc: Document) => {
        const textareas = clonedDoc.querySelectorAll<HTMLTextAreaElement>("textarea");
        for (const ta of Array.from(textareas)) {
          const live = document.querySelector<HTMLTextAreaElement>(`textarea[name="${ta.getAttribute("name") ?? ""}"]`) ?? ta;
          const rect = live.getBoundingClientRect();
          const div = clonedDoc.createElement("div");
          div.textContent = ta.value;
          div.style.width = `${rect.width}px`;
          div.style.minHeight = `${rect.height}px`;
          div.style.padding = window.getComputedStyle(live).padding;
          div.style.font = window.getComputedStyle(live).font;
          div.style.lineHeight = window.getComputedStyle(live).lineHeight;
          div.style.color = window.getComputedStyle(live).color;
          div.style.background = window.getComputedStyle(live).background;
          div.style.border = window.getComputedStyle(live).border;
          div.style.borderRadius = window.getComputedStyle(live).borderRadius;
          div.style.whiteSpace = "pre-wrap";
          div.style.wordWrap = "break-word";
          div.style.overflowWrap = "break-word";
          div.style.boxSizing = "border-box";
          ta.parentNode?.replaceChild(div, ta);
        }
      },
    });

    // Crop the full-document capture to just the visible viewport so the
    // screenshot reflects what the user actually saw when filing feedback.
    const dpr = window.devicePixelRatio || 1;
    const cropX = Math.floor(window.scrollX * dpr);
    const cropY = Math.floor(window.scrollY * dpr);
    const cropW = Math.min(Math.floor(window.innerWidth * dpr), fullCanvas.width - cropX);
    const cropH = Math.min(Math.floor(window.innerHeight * dpr), fullCanvas.height - cropY);

    const cropped = document.createElement("canvas");
    cropped.width = cropW;
    cropped.height = cropH;
    const ctx = cropped.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(fullCanvas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

    const dataUrl = cropped.toDataURL("image/png");
    const idx = dataUrl.indexOf(",");
    if (idx < 0) return null;
    return { mime_type: "image/png", base64_bytes: dataUrl.slice(idx + 1) };
  } catch (err) {
    // Surface the underlying failure to the developer console so we can
    // diagnose why html2canvas chokes on a particular page (CSS feature it
    // doesn't support, cross-origin image, OOM, etc.).
    // eslint-disable-next-line no-console
    console.warn("[atlas-feedback] screenshot capture failed:", err);
    return null;
  }
}
